import React, { useCallback, useEffect, useState } from 'react';
import getRewardfulCoupon from '../lib/getRewardfulCoupon';
import useCheckoutCoupon from './useCheckoutCoupon';
import useCheckoutState from './useCheckoutState';
import getRewardfulReferral from '../lib/getRewardfulReferral';

const RewardfulContext = React.createContext();

// Rewardful referrals are UUIDs. Only the generic shape is checked (not
// version/variant bits) so we stay compatible if Rewardful changes how
// they generate them, while still rejecting unrelated ?referral= values
// like ?referral=blog-post that other tools put on URLs.
const REWARDFUL_REFERRAL_UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function RewardfulProvider({ children }) {
  // only handle rewardful coupon once
  const [handledRewardfulCoupon, setHandledRewardfulCoupon] = useState(false);

  const { setShowCouponCodeField, query } = useCheckoutState();

  const { validateCoupon, getCouponByStripeCouponId } = useCheckoutCoupon();

  const rewardfulCoupon = getRewardfulCoupon();

  useEffect(() => {
    async function setCoupon(couponCode) {
      try {
        await validateCoupon(couponCode);
        setShowCouponCodeField(true);
      } catch (error) {
        // no-op
      }
    }

    async function getAndApplyCoupon(rewardfulCoupon) {
      try {
        const coupon = await getCouponByStripeCouponId(rewardfulCoupon.id);
        setCoupon(coupon.code);
      } catch (error) {
        // no-op
      }
    }

    if (rewardfulCoupon && handledRewardfulCoupon === false) {
      getAndApplyCoupon(rewardfulCoupon);
      setHandledRewardfulCoupon(true);
    }
  }, [
    rewardfulCoupon,
    handledRewardfulCoupon,
    getCouponByStripeCouponId,
    setShowCouponCodeField,
    validateCoupon,
  ]);

  // Resolved at call time (not mount time): the Rewardful snippet is injected
  // via tracking pixels after the checkout has mounted, so the referral only
  // becomes available once rw.js has loaded
  const getRewardfulReferralId = useCallback(() => {
    const rewardfulReferralId = getRewardfulReferral();
    if (rewardfulReferralId) {
      return rewardfulReferralId;
    }

    // rewardful_referral is passed into the iframe by our embed and overlay code
    if (query.rewardful_referral) {
      return query.rewardful_referral;
    }

    // Rewardful's cross-domain tracking decorates inbound links with
    // referral=<uuid>; reading it directly also covers rw.js being blocked
    if (query.referral && REWARDFUL_REFERRAL_UUID_REGEX.test(query.referral)) {
      return query.referral;
    }

    return undefined;
  }, [query]);

  return (
    <RewardfulContext.Provider
      value={{
        rewardfulCoupon,
        handledRewardfulCoupon,
        getRewardfulReferralId,
      }}
    >
      {children}
    </RewardfulContext.Provider>
  );
}

export default function useRewardful() {
  const context = React.useContext(RewardfulContext);

  if (context === undefined) {
    throw new Error('useRewardful must be used within a RewardfulProvider');
  }

  return context;
}

export { RewardfulProvider };
